<template>
  <v-card class="rounded-xl mt-4" :border="false" :elevation="0">
    <v-row>
      <v-col md="12">
        <h1>{{ $t('FILE_STEP_GUARANTEE.TITLE') }}</h1>
      </v-col>
    </v-row>

    <!-- SEARCH SERIAL NUMBER -->
    <v-row v-if="mounted" class="serial-number-card">
      <v-col md="8">
        <h2 class="serial-number-content-title mb-5">{{ $t('FILE_STEP_GUARANTEE.SUB_TITLE') }}</h2>
        <template v-if="product && product.modelSerialNumber">
          <p class="serial-number-content-card">
            {{ product.modelSerialNumber.description }}
          </p>
          <p class="serial-number-content-card">
            {{ product.modelSerialNumber.errorMessage }}
          </p>
        </template>
        <template v-else>
          <p class="serial-number-content-card">
            {{ $t('FILE_STEP_GUARANTEE.TEXT.SERIAL_NUMBER_EXAMPLE_ONE') }}
          </p>
          <p class="serial-number-content-card">
            {{ $t('FILE_STEP_GUARANTEE.TEXT.SERIAL_NUMBER_EXAMPLE_TWO') }}
          </p>
        </template>
      </v-col>

      <v-col md="4">
        <div v-if="!hasSerialNumber && serialNumberRequiredLength" class="mb-4">
          <p class="warning-message text-center">
            {{ $t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_UNKNOWN') }}
          </p>
        </div>

        <v-text-field
          v-model="search"
          :placeholder="$t('FILE_STEP_GUARANTEE.PLACEHOLDER.SEARCH')"
          :rules="requiredRules"
          required
          :error-messages="error?.search"
          :messages="warning?.search"
          hide-details="auto"
        >
          <template v-slot:append-inner>
            <img
              v-if="error?.search"
              alt="dialog-icon"
              class="mr-2"
              src="@/assets/icons/file/calendar_wrong.svg"
            />
          </template>
          <template v-slot:message="{ message }">
            <img alt="dialog-icon" class="red-cross-svg" src="@/assets/icons/file/cross.svg" />
            {{ message }}
          </template>
        </v-text-field>

        <div v-if="product && product.serialNumberPicture && !isSerialNumberValid && serialNumberRequiredLength" class="mt-4">
          <v-img
            class="dialog-picture-content"
            :src="product.serialNumberPicture"
            alt="Product Preview"
          />
        </div>
      </v-col>
    </v-row>

    <v-row v-if="productDistributorRefList.length > 0">
      <v-col cols="12">
        <v-divider class="my-2" />
      </v-col>
      <v-col md="12">
        <p class="content-title mb-1">{{ $t('FILE_STEP_GUARANTEE.DISTRIBUTOR_REF') }}</p>
        <p>
          <span v-for="(item, index) in productDistributorRefList" :key="index">
            {{ item.reference }}<em v-if="index !== productDistributorRefList.length - 1">, </em>
          </span>
        </p>
      </v-col>
    </v-row>

    <!-- PURCHASE DATE -->
    <v-row v-if="showPurchaseDate">
      <v-col cols="12">
        <v-divider class="my-2" />
      </v-col>

      <v-col md="6">
        <p require-field="true" class="content-title mb-5">{{ $t('FILE_STEP_GUARANTEE.DATE') }}</p>
        <CustomDateInput
          :model-value="purchaseDate"
          :max-date="today"
          @update:modelValue="purchaseDate = $event"
        />
      </v-col>

      <v-col
        v-if="!showPurchaseDateFileInput && !showGuaranteeExtension"
        md="6"
        class="d-flex justify-end align-self-end"
      >
        <div class="btn-file-repair mb-2" @click="showGuaranteeExtension = true">
          {{ $t('FILE_STEP_GUARANTEE.BUTTON.UNKNOWN_DATE') }}
        </div>
      </v-col>

      <v-col v-if="!isDateExpired && showPurchaseDateFileInput">
        <p class="content-title mb-5">{{ $t('FILE_STEP_GUARANTEE.PROOF_PURCHASE_DATE') }}</p>
        <v-row>
          <v-col cols="12" md="6" class="pr-1">
            <v-text-field
              :label="$t('FILE_STEP_GUARANTEE.INPUT_LABEL_BY_QR_CODE')"
              :disabled="currentRepairFile.product_buy_proof_file_path"
              :hide-details="!error?.purchaseDateFile"
              append-inner-icon="mdi-camera"
              @click="openAttachmentQrCodeModal('PURCHASE_PROOF', purchaseDateFile)"
            />
          </v-col>
          <v-col cols="12" md="6" class="pl-1">
            <v-text-field
              :label="$t('FILE_STEP_GUARANTEE.INPUT_LABEL_BY_PC')"
              :disabled="currentRepairFile.product_buy_proof_file_path"
              :hide-details="!error?.purchaseDateFile"
              append-inner-icon="mdi-download"
              @click="openAttachmentPcModal('PURCHASE_PROOF', purchaseDateFile)"
            />
          </v-col>
        </v-row>

        <!--Chips file added-->
        <v-row class="chip-container pb-3" v-if="currentRepairFile.product_buy_proof_file_path">
          <v-col>
            <v-chip append-icon="mdi-close" class="cursor-pointer">
              {{ currentRepairFile.product_buy_proof_file_path }}
              <template v-slot:append>
                <v-icon
                  @click="removeAttachment('PURCHASE_PROOF', currentRepairFile.technical_id)"
                  class="append-icon"
                >
                  mdi-close
                </v-icon>
              </template>
            </v-chip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- GUARANTEE EXTENSION DATE -->
    <v-row v-if="showGuaranteeExtension" class="extension-guarantee-date-row-container">
      <v-col md="6">
        <p require-field="true" class="content-title mb-5">
          {{ $t('FILE_STEP_GUARANTEE.END_DATE_EXTENSION_GUARANTEE') }}
        </p>
        <CustomDateInput
          :model-value="guaranteeExtensionDate"
          @update:modelValue="guaranteeExtensionDate = $event"
        />
      </v-col>
      <v-col
        v-if="!showGuaranteeExtensionFileInput && !forceGuarantee"
        md="6"
        class="d-flex justify-end align-self-end"
      >
        <div class="btn-file-repair mb-2" @click="setForcedGuarantee">
          {{ $t('FILE_STEP_GUARANTEE.BUTTON.WITHOUT_EXTENSION') }}
        </div>
      </v-col>

      <v-col v-if="showGuaranteeExtensionFileInput">
        <p class="content-title mb-5">
          {{ $t('FILE_STEP_GUARANTEE.PROOF_EXTENSION_GUARANTEE') }}
        </p>
        <v-row>
          <v-col cols="12" md="6" class="pr-1">
            <v-text-field
              :label="$t('FILE_STEP_GUARANTEE.INPUT_LABEL_BY_QR_CODE')"
              :disabled="currentRepairFile.product_guarantee_extension_proof_file_path"
              :hide-details="!error?.guaranteeExtensionFile"
              append-inner-icon="mdi-camera"
              @click="
                openAttachmentQrCodeModal('GUARANTEE_EXTENSION_PROOF', guaranteeExtensionFile)
              "
            />
          </v-col>
          <v-col cols="12" md="6" class="pl-1">
            <v-text-field
              :label="$t('FILE_STEP_GUARANTEE.INPUT_LABEL_BY_PC')"
              :disabled="currentRepairFile.product_guarantee_extension_proof_file_path"
              :hide-details="!error?.guaranteeExtensionFile"
              append-inner-icon="mdi-download"
              @click="openAttachmentPcModal('GUARANTEE_EXTENSION_PROOF', guaranteeExtensionFile)"
            />
          </v-col>
        </v-row>

        <!--Chips file added-->
        <v-row
          class="chip-container pb-3"
          v-if="currentRepairFile.product_guarantee_extension_proof_file_path"
        >
          <v-col>
            <v-chip append-icon="mdi-close" class="clickable-chip">
              {{ currentRepairFile.product_guarantee_extension_proof_file_path }}
              <template v-slot:append>
                <v-icon
                  @click="
                    removeAttachment('GUARANTEE_EXTENSION_PROOF', currentRepairFile.technical_id)
                  "
                  class="append-icon"
                  icon="mdi-close"
                />
              </template>
            </v-chip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!--WARNING/SUCCESS MESSAGE-->
    <v-row v-if="showGuaranteeFooter">
      <v-col cols="12">
        <v-divider class="my-2" />
      </v-col>
      <v-col cols="12">
        <div v-if="isUnderGuarantee" class="d-flex align-center">
          <p class="success-title mr-5">{{ $t('FILE_STEP_GUARANTEE.SUCCESS_TITLE') }}</p>
          <img
            class="cross-round-svg"
            alt="check-success-icon"
            style="height: 20px; width: 25px"
            src="@/assets/icons/file/check_round.svg"
          />
        </div>

        <div v-else class="d-flex align-center">
          <p class="warning-title mr-3">{{ $t('FILE_STEP_GUARANTEE.WARNING_TITLE') }}</p>
          <img
            alt="check-failed-icon"
            style="height: 20px; width: 25px"
            src="@/assets/icons/file/cross-red.svg"
          />
        </div>
      </v-col>
      <v-col md="4">
        <v-row class="d-flex flex-row">
          <v-col md="12" class="pb-0">
            <p class="content-title mr-5">{{ $t('FILE_STEP_GUARANTEE.GUARANTEE_TIME') }}</p>
          </v-col>
          <v-col
            v-if="
              currentRepairFile?.product_commercial_guarantee &&
              purchaseDate &&
              !guaranteeExtensionDate
            "
            md="12"
          >
            {{
              currentRepairFile.product_commercial_guarantee > 0
                ? `${currentRepairFile.product_commercial_guarantee} ${$t(
                    'FILE_STEP_GUARANTEE.DAYS'
                  )}`
                : $t('FILE_STEP_GUARANTEE.DAY')
            }}
          </v-col>
          <v-col
            v-else-if="currentRepairFile?.product_extension_guarantee && guaranteeExtensionDate"
            md="12"
          >
            {{
              currentRepairFile.product_extension_guarantee > 0
                ? `${currentRepairFile.product_extension_guarantee} ${$t(
                    'FILE_STEP_GUARANTEE.DAYS'
                  )}`
                : $t('FILE_STEP_GUARANTEE.DAY')
            }}
          </v-col>
          <v-col v-else md="12">
            <div v-if="!currentRepairFile.product_commercial_guarantee">
              {{ $t('FILE_STEP_GUARANTEE.NO_DAY') }}
            </div>
            <div v-else>
              {{
                currentRepairFile.product_commercial_guarantee > 0
                  ? `${currentRepairFile.product_commercial_guarantee} ${$t(
                      'FILE_STEP_GUARANTEE.DAYS'
                    )}`
                  : $t('FILE_STEP_GUARANTEE.DAY')
              }}
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col md="4">
        <v-row class="d-flex flex-row">
          <v-col md="12" class="pb-0">
            <p class="content-title mr-5">
              {{ $t('FILE_STEP_GUARANTEE.GUARANTEE_TIME_REMAINING') }}
            </p>
          </v-col>
          <v-col v-if="showMessageLastDay" md="12">
            {{ $t('FILE_STEP_GUARANTEE.TODAY') }}
          </v-col>
          <v-col v-else-if="!showMessageLastDay && remainingDays" md="12">
            {{ remainingDays < 0 ? 0 : remainingDays }}
            {{
              remainingDays === 1 ? $t('FILE_STEP_GUARANTEE.DAY') : $t('FILE_STEP_GUARANTEE.DAYS')
            }}
          </v-col>
          <v-col v-else md="12">
            {{ $t('FILE_STEP_GUARANTEE.NO_DAY') }}
          </v-col>
        </v-row>
      </v-col>
      <v-col md="4">
        <v-row class="d-flex flex-row">
          <v-col class="d-flex justify-end" md="12" v-if="isDateExpired || forceGuarantee">
            <v-btn
              class="boom-button-transparent pl-5 pr-5"
              variant="outlined"
              @click="openEnforceSupportModal"
            >
              {{ $t('FILE_STEP_GUARANTEE.BUTTON.ENFORCE_SUPPORT') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>

  <v-btn
    class="boom-button mt-4 pl-15 pr-15"
    variant="outlined"
    :disabled="!canContinue"
    @click="submit"
  >
    {{ $t('FILE_STEP_GUARANTEE.BUTTON.CONTINUE') }}
    <img alt="arrow_small-icon" class="arrow-svg" src="@/assets/icons/file/arrow_small_right.svg" />
  </v-btn>

  <EnforceSupportModal ref="enforceSupportModal" />
  <GuaranteeExclusionModal ref="guaranteeExclusionModal" />
  <AddAttachmentPCModal ref="addAttachmentPcModal" />
  <AddAttachmentQRCodeModal ref="addAttachmentQrCodeModal" />
</template>

<script>
import EnforceSupportModal from '@/components/modal/repair-file/EnforceSupportModal.vue'
import ProductService from '@/services/ProductService'
import GuaranteeExtensionService from '@/services/GuaranteeExtensionService'
import GuaranteeExclusionModal from '@/components/modal/repair-file/GuaranteeExclusionModal.vue'
import RepairFileService from '@/services/RepairFileService'
import { auth as $store } from '@/store/AuthModule'
import {
  displayToast,
  handleResponseError,
  convertDateFromInput
} from '@/components/utils/globalFunction'
import ModelSerialNumberService from '@/services/ModelSerialNumberService'
import ResponseConstant from '@/constants/responseConstant'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'
import RepairFileGuaranteeConstant from '@/constants/repairFileGuaranteeConstant'
import GuaranteeExtensionBoomerangService from '@/services/GuaranteeExtensionBoomerangService'
import { RepairFile } from '@/models/RepairFile'
import AddAttachmentPCModal from '@/components/modal/repair-file/step-guarantee/AddAttachmentPcModal.vue'
import AddAttachmentQRCodeModal from '@/components/modal/repair-file/step-guarantee/AddAttachmentQRCodeModal.vue'
import Validator from '@/components/utils/validator'
import CustomDateInput from '@/components/ui/CustomDateInput.vue'

export default {
  name: 'StepGuaranteePage',
  components: {
    CustomDateInput,
    AddAttachmentQRCodeModal,
    AddAttachmentPCModal,
    GuaranteeExclusionModal,
    EnforceSupportModal
  },
  props: {
    currentStep: Number,
    currentRepairFile: RepairFile
  },
  data() {
    return {
      mounted: false,
      hasSerialNumber: false,
      serialNumberRequiredLength: null,

      today: new Date(),
      product: null,
      modelSerialNumber: null,
      search: '',
      warning: null,
      error: null,

      purchaseDate: null,
      guaranteeExtensionDate: null,

      resetGuaranteeExtensionDate: false,
      showGuaranteeExtension: false,
      isDateExpired: false,
      showMessageLastDay: false,

      forceGuarantee: false,

      remainingDays: null,
      guaranteeExtension: null,
      guaranteeExtensionBoomerang: null,

      forcedSupportDescription: '',
      forcedSupportFile: null,

      showPurchaseDateFileInput: false,
      purchaseDateFile: null,
      showGuaranteeExtensionFileInput: false,
      guaranteeExtensionFile: null,

      productDistributorRefList: [],

      requiredRules: Validator.requiredRules
    }
  },
  async created() {
    try {
      if (this.currentRepairFile.product_boomerang_id) {
        this.product = await ProductService.getProduct(this.currentRepairFile.product_boomerang_id)
        this.patchUnsavedVariables()

        // If purchaseDate already set from currentRepairFile, use it for calculate remainingDays
        if (this.purchaseDate) {
          this.calculateCommercialGuaranteeRemainingDays()
        }
        // Load filePath of currentRepairFile if it exists
        this.purchaseDateFile = this.currentRepairFile.product_buy_proof_file_path

        // If guaranteeExtensionDate already set from currentRepairFile, use it for calculate remainingDays
        if (this.guaranteeExtensionDate) {
          this.remainingDays = this.getRemainingDays(this.guaranteeExtensionDate)
        }
        // Load filePath of currentRepairFile if it exists
        this.guaranteeExtensionFile =
          this.currentRepairFile.product_guarantee_extension_proof_file_path

        // Load filePath of currentRepairFile if it exists
        this.forcedSupportFile = this.currentRepairFile.product_force_support_proof_file_path

        if (this.currentRepairFile.network_boomerang_id) {
          const response = await ProductService.getProductDistributorRefList(
            this.currentRepairFile.network_boomerang_id,
            this.currentRepairFile.product_boomerang_id
          )
          this.productDistributorRefList = response.item_list
        }
      } else {
        this.modelSerialNumber = await ModelSerialNumberService.getBySubCategory(
          this.currentRepairFile.sub_category_boomerang_id
        )
      }

      if (this.modelSerialNumber || (this.product && this.product?.modelSerialNumber?.format)) {
        this.hasSerialNumber = true
      }

      if (this.currentRepairFile.product_boomerang_id) {
        this.serialNumberRequiredLength = this.product?.modelSerialNumber?.format
      } else if (this.modelSerialNumber?.format) {
        this.serialNumberRequiredLength = this.modelSerialNumber?.format
      } else {
        this.serialNumberRequiredLength = false
      }
    } catch (error) {
      handleResponseError(error)
    } finally {
      this.mounted = true
    }
  },
  watch: {
    search(newValue) {
      if (this.serialNumberRequiredLength) {
        if (newValue.length === 0) {
          this.error = null
          this.warning = null
          this.guaranteeExtension = null
          this.guaranteeExtensionBoomerang = null
          this.showGuaranteeExtension = false
        }

        if (newValue.length !== Number(this.serialNumberRequiredLength)) {
          this.error = {
            search: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_GOOD_FORMAT', {
              value: this.serialNumberRequiredLength
            })
          }
          this.forcedSupportDescription = ''
          this.forceGuarantee = false
          this.purchaseDate = null
          this.guaranteeExtension = null
          this.guaranteeExtensionDate = null
          this.isDateExpired = false
          this.showGuaranteeExtension = false
        }

        if (newValue.length === Number(this.serialNumberRequiredLength)) {
          this.getGuaranteeExtension()
        }
      } else {
        this.getGuaranteeExtension()
      }
    },
    purchaseDate(newValue) {
      if (this.guaranteeExtensionDate) {
        this.resetCommercialGuaranteeState()
        this.resetGuaranteeExtensionDate = true
        this.guaranteeExtensionDate = null
      }

      if (newValue) {
        this.checkCommercialGuarantee()
      } else {
        this.resetCommercialGuaranteeState()
      }
    },
    guaranteeExtensionDate(newValue) {
      if (this.resetGuaranteeExtensionDate) {
        this.resetGuaranteeExtensionDate = false
        this.showGuaranteeExtensionFileInput = false
        return
      }

      if (newValue) {
        this.checkGuaranteeExtension()
      } else {
        this.resetGuaranteeExtensionState()
      }
    }
  },
  computed: {
    currentCollaborator() {
      return $store.state.currentCollaborator
    },
    isSerialNumberValid() {
      return this.isSerialNumberInFormat(this.search)
    },
    hasGuaranteeExtension() {
      return (
        this.remainingDays &&
        (this.guaranteeExtension?.end_guarantee || this.guaranteeExtensionBoomerang?.end_guarantee)
      )
    },
    showPurchaseDate() {
      return !this.guaranteeExtension?.end_guarantee && this.isSerialNumberValid
    },
    showGuaranteeFooter() {
      return (
        this.hasGuaranteeExtension ||
        (this.purchaseDate && !this.isDateExpired) ||
        this.guaranteeExtensionDate ||
        (this.remainingDays && this.isDateExpired && this.guaranteeExtensionDate) ||
        this.forceGuarantee
      )
    },
    isUnderGuarantee() {
      return (
        (this.remainingDays && this.remainingDays >= 0) ||
        this.showMessageLastDay ||
        (this.forceGuarantee && this.forcedSupportDescription.length !== 0) ||
        this.forcedSupportDescription.length !== 0
      )
    },
    canContinue() {
      return (
        (this.hasGuaranteeExtension && this.isSerialNumberValid) ||
        (this.purchaseDate && this.isSerialNumberValid && !this.isDateExpired) ||
        (this.guaranteeExtensionDate && this.isSerialNumberValid) ||
        (this.remainingDays && !this.isDateExpired && this.isSerialNumberValid) ||
        (this.forceGuarantee && this.isSerialNumberValid)
      )
    }
  },
  methods: {
    convertDateFromInput,
    patchUnsavedVariables() {
      let forceGuaranteeSet = false

      if (
        this.currentRepairFile.status_detail ===
          RepairFileStatusDetailConstant.GUARANTEE_EVALUATED &&
        !this.currentRepairFile.product_buy_date &&
        !this.currentRepairFile.product_guarantee_extension_date &&
        !this.currentRepairFile.product_force_support_description
      ) {
        if (this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE) {
          this.forceGuarantee = true
        }
      } else {
        this.forceGuarantee = this.forcedSupportDescription.length !== 0
        forceGuaranteeSet = true
      }

      if (this.currentRepairFile.product_buy_date) {
        this.purchaseDate = new Date(this.currentRepairFile.product_buy_date)
      }

      if (this.currentRepairFile.product_guarantee_extension_date) {
        this.guaranteeExtensionDate = new Date(
          this.currentRepairFile.product_guarantee_extension_date
        )
        this.showGuaranteeExtension = true
        this.purchaseDate = null
      }

      this.search = this.currentRepairFile.product_serial_number || ''

      this.forcedSupportDescription = this.currentRepairFile.product_force_support_description || ''
      if (forceGuaranteeSet) {
        this.forceGuarantee = this.forcedSupportDescription.length !== 0
      }
    },
    isSerialNumberInFormat(searchInput) {
      if (!this.serialNumberRequiredLength) return !!searchInput

      if (this.currentRepairFile.product_boomerang_id) {
        return searchInput.length === parseInt(this.product?.modelSerialNumber?.format)
      } else if (this.modelSerialNumber?.format) {
        return searchInput.length === parseInt(this.modelSerialNumber?.format)
      } else {
        return (
          !this.currentRepairFile.product_boomerang_id &&
          !this.modelSerialNumber &&
          searchInput.length
        )
      }
    },
    setForcedGuarantee() {
      this.forceGuarantee = true
      this.remainingDays = 0
    },
    resetCommercialGuaranteeState() {
      this.remainingDays = null
      this.isDateExpired = false
      this.showPurchaseDateFileInput = false
      this.showGuaranteeExtension = false
    },
    resetGuaranteeExtensionState() {
      this.remainingDays = null
      this.showGuaranteeExtensionFileInput = false
      this.isDateExpired = false
    },
    async getGuaranteeExtension() {
      if (this.isSerialNumberValid) {
        this.guaranteeExtension = null
        this.guaranteeExtensionBoomerang = null

        try {
          this.guaranteeExtension =
            await GuaranteeExtensionService.getExtensionBySerialNumberAndProductBoomerangId(
              this.search,
              this.currentRepairFile.product_boomerang_id
            )
        } catch (error) {
          this.error = null
          this.warning = {
            search: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_FOUND_FOR_EXTENSION')
          }
        }

        try {
          this.guaranteeExtensionBoomerang =
            await GuaranteeExtensionBoomerangService.getExtensionBySerialNumberAndProductBoomerangId(
              this.search,
              this.currentRepairFile.product_boomerang_id
            )
        } catch (error) {
          this.warning = {
            search: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_FOUND_FOR_PROLONGATION')
          }
        }

        if (this.guaranteeExtension || this.guaranteeExtensionBoomerang) {
          let longest_end_guarantee_date
          if (this.guaranteeExtension && this.guaranteeExtensionBoomerang) {
            longest_end_guarantee_date =
              this.guaranteeExtension.end_guarantee > this.guaranteeExtensionBoomerang.end_guarantee
                ? this.guaranteeExtension.end_guarantee
                : this.guaranteeExtensionBoomerang.end_guarantee
          } else if (this.guaranteeExtension) {
            longest_end_guarantee_date = this.guaranteeExtension.end_guarantee
          } else {
            longest_end_guarantee_date = this.guaranteeExtensionBoomerang.end_guarantee
          }

          // Update this.remainingDays only if we change serial number after page already loaded.
          if (this.mounted) {
            this.remainingDays = this.getRemainingDays(longest_end_guarantee_date)
            this.showGuaranteeExtension = false
          }

          this.isDateExpired = this.remainingDays <= 0
          this.error = null
          this.warning = null
        } else {
          if (this.mounted) {
            this.error = null
            this.warning = {
              search: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_FOUND_FOR_PROLONGATION')
            }
            if (!this.currentRepairFile.product_buy_date) {
              this.purchaseDate = null
            }
            if (!this.currentRepairFile.product_guarantee_extension_date) {
              this.guaranteeExtensionDate = null
            }
            this.isDateExpired = false
            this.showGuaranteeExtension = false
            this.guaranteeExtension = null
            this.guaranteeExtensionBoomerang = null
          }
        }
      }

      if (this.mounted) {
        this.forcedSupportDescription = ''
        this.forceGuarantee = false
        this.showMessageLastDay = false
        this.showGuaranteeExtension = false
      }
    },
    getRemainingDays(date) {
      const baseDate = new Date(date)
      baseDate.setHours(0, 0, 0, 0)
      const timeDifference = baseDate - this.today.setHours(0, 0, 0, 0)
      return Math.floor(timeDifference / (1000 * 60 * 60 * 24))
    },
    calculateCommercialGuaranteeRemainingDays() {
      const baseDate = new Date(this.purchaseDate)
      baseDate.setHours(0, 0, 0, 0)
      baseDate.setDate(baseDate.getDate() + this.currentRepairFile.product_commercial_guarantee)

      this.remainingDays = this.getRemainingDays(baseDate)
      return baseDate
    },
    checkCommercialGuarantee() {
      const endDate = this.calculateCommercialGuaranteeRemainingDays()

      if (endDate < this.today) {
        // GUARANTEE EXPIRED
        this.forcedSupportDescription = ''
        this.forceGuarantee = false
        this.showPurchaseDateFileInput = false
        this.isDateExpired = true
        this.showMessageLastDay = false
        this.showGuaranteeExtension = true
      } else if (endDate > this.today) {
        // UNDER GUARANTEE
        this.forcedSupportDescription = ''
        this.forceGuarantee = false
        this.showPurchaseDateFileInput = true
        this.showMessageLastDay = false
        this.isDateExpired = false
        this.showGuaranteeExtension = false
      } else {
        // UNDER LAST DAY GUARANTEE
        this.isDateExpired = false
        this.showMessageLastDay = true
        this.showGuaranteeExtension = false
      }
    },
    checkGuaranteeExtension() {
      this.remainingDays = this.getRemainingDays(this.guaranteeExtensionDate)

      if (this.remainingDays < 0) {
        // GUARANTEE EXTENSION EXPIRED
        this.forcedSupportDescription = ''
        this.forceGuarantee = false
        this.showGuaranteeExtensionFileInput = false
        this.isDateExpired = true
        this.showMessageLastDay = false
      } else if (this.remainingDays > 0) {
        // UNDER GUARANTEE EXTENSION
        this.forcedSupportDescription = ''
        this.forceGuarantee = false
        this.showGuaranteeExtensionFileInput = true
        this.showMessageLastDay = false
        this.isDateExpired = false
      } else {
        // UNDER LAST DAY GUARANTEE EXTENSION
        this.showMessageLastDay = true
      }
    },
    calculateDate(selectedDate, extensionDate) {
      const selectDate = new Date(selectedDate)
      selectDate.setHours(0, 0, 0, 0)
      return selectDate.setDate(selectDate.getDate() + extensionDate)
    },
    async submit() {
      if (this.product?.modelSerialNumber?.guaranteeExclusion) {
        const message = this.product.modelSerialNumber.guaranteeExclusion
        const response = await this.$refs.guaranteeExclusionModal.show({
          message: message
        })
        if (response.confirmed) {
          await this.formatUpdateData()
        }
      } else {
        await this.formatUpdateData()
      }
    },
    async formatUpdateData() {
      let data = {
        status_detail: RepairFileStatusDetailConstant.GUARANTEE_EVALUATED,
        is_complete_update: 'True',
        serial_number: this.search,
        requestor_collaborator_id: this.currentCollaborator.technical_id
      }
      const formData = new FormData()
      // ACCEPT BY SERIAL NUMBER
      if (
        this.search.length !== 0 &&
        ((this.guaranteeExtension &&
          this.getRemainingDays(this.guaranteeExtension.end_guarantee) >= 0) ||
          (this.guaranteeExtensionBoomerang &&
            this.getRemainingDays(this.guaranteeExtensionBoomerang.end_guarantee) >= 0))
      ) {
        data['guarantee_type'] = 'serial_number'
      }
      // ACCEPT BY GUARANTEE EXTENSION
      else if (this.guaranteeExtensionDate && this.remainingDays >= 0) {
        data['guarantee_type'] = 'extension'
        data['extension_date'] = convertDateFromInput(this.guaranteeExtensionDate)
        data['buy_date'] = convertDateFromInput(this.purchaseDate)
      }
      // ACCEPT BY COMMERCIAL
      else if (
        this.purchaseDate &&
        this.getRemainingDays(
          this.calculateDate(this.purchaseDate, this.currentRepairFile.product_commercial_guarantee)
        ) >= 0
      ) {
        data['guarantee_type'] = 'commercial'
        data['buy_date'] = convertDateFromInput(this.purchaseDate)
        data['extension_date'] = convertDateFromInput(this.guaranteeExtensionDate)
      }
      // ACCEPT BY FORCE GUARANTEE
      else if (this.forcedSupportDescription) {
        data['guarantee_type'] = 'force_guarantee'
        data['force_support_description'] = this.forcedSupportDescription
      } else {
        data['guarantee_type'] = 'not_guarantee'
        data['buy_date'] = null
        data['extension_date'] = null
      }
      formData.append('data', JSON.stringify(data))
      await this.updateStepGuarantee(formData)
    },
    async updatePartialData() {
      let data = {
        status_detail: RepairFileStatusDetailConstant.GUARANTEE_EVALUATED,
        requestor_collaborator_id: this.currentCollaborator.technical_id
      }
      const formData = new FormData()
      data['force_support_description'] = this.forcedSupportDescription || ''
      data['extension_date'] = !this.forcedSupportDescription
        ? convertDateFromInput(this.guaranteeExtensionDate) || null
        : null
      data['buy_date'] = !this.forcedSupportDescription
        ? convertDateFromInput(this.purchaseDate) || null
        : null
      data['serial_number'] = this.search || ''

      formData.append('data', JSON.stringify(data))
      await this.updateStepGuarantee(formData, true)
    },
    async updateStepGuarantee(formData, isPartial = false) {
      const repairFileId = this.currentRepairFile.technical_id
      try {
        await RepairFileService.update(repairFileId, formData)
        if (isPartial === false) {
          this.$emit('change-step', this.currentStep + 1)
        }
      } catch (error) {
        handleResponseError(error)
      }
    },
    async openEnforceSupportModal() {
      const response = await this.$refs.enforceSupportModal.show({
        currentRepairFile: this.currentRepairFile,
        forcedSupportDescription: this.forcedSupportDescription,
        forcedSupportFile: this.forcedSupportFile
      })
      if (response.confirmed) {
        this.forcedSupportDescription = response.forcedSupportDescription
      }
    },
    async openAttachmentPcModal(attachmentType, modelValue) {
      const response = await this.$refs.addAttachmentPcModal.show({
        currentRepairFile: this.currentRepairFile,
        attachmentType: attachmentType,
        attachmentFile: modelValue
      })

      if (response?.confirmed) {
        // Emit event for refresh currentRepairFile after add attachment
        this.$emit('refresh-repair-file')
      }
    },
    async openAttachmentQrCodeModal(attachmentType, modelValue) {
      const response = await this.$refs.addAttachmentQrCodeModal.show({
        currentRepairFile: this.currentRepairFile,
        attachmentType: attachmentType,
        attachmentFile: modelValue
      })

      if (response?.confirmed) {
        // Emit event for refresh currentRepairFile after add attachment
        this.$emit('refresh-repair-file')
      }
    },
    async removeAttachment(attachmentType, repairFileId) {
      this.loading = true
      try {
        await RepairFileService.removeAttachmentFromRepairFile(
          repairFileId,
          attachmentType,
          this.currentCollaborator.technical_id
        )
        displayToast(this.$t('COMMON.MESSAGE.REMOVE_ATTACHMENT_SUCCESS'), 'success')
        // Emit event for refresh currentRepairFile after remove attachment
        this.$emit('refresh-repair-file')
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 24px;
}

.arrow-svg {
  height: 100%;
  width: 50%;
  filter: invert(89%) sepia(98%) saturate(1%) hue-rotate(266deg) brightness(110%) contrast(97%);
}

.red-cross-svg {
  height: 11px;
  width: 15px;
  filter: invert(49%) sepia(71%) saturate(2395%) hue-rotate(326deg) brightness(100%) contrast(102%);
}

.boom-button-transparent:hover .arrow-svg {
  filter: invert(89%) sepia(98%) saturate(1%) hue-rotate(266deg) brightness(110%) contrast(97%);
}

.content-title {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.serial-number-card {
  padding: 6px;
  border-radius: 20px;
  background-color: #fafafa;
}

.serial-number-content-title {
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.serial-number-content-card {
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.warning-title {
  color: var(--error-color);
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.success-title {
  color: var(--default-primary);
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.line-top {
  margin: 0 0 0 0;
  border-top: 1px solid #dadada;
}

.cross-round-svg {
  height: 15px;
  filter: invert(47%) sepia(62%) saturate(571%) hue-rotate(50deg) brightness(96%) contrast(90%);
}

.warning-message {
  color: var(--warning-color);
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: normal;
}

.chip-container {
  margin-left: 0;
}
</style>
